import { format, parse } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, FilterIcon, XIcon } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";

import { Button, Card, CardContent } from "@/components";
import { Calendar } from "@/components/ui/calendar";
import { Combobox } from "@/components/ui/combobox";
import { FloatingLabelInput } from "@/components/ui/floating-label-input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useI18n } from "@/context";
import { cn } from "@/lib/utils";

import { TReportsFiltersFormData } from "../../reports.schema";

interface IReportsFiltersProps {
  form: UseFormReturn<TReportsFiltersFormData>;
  onApplyFilters: (data: TReportsFiltersFormData) => void;
  onClearFilters: () => void;
  statusOptions: Array<{ value: string; label: string }>;
  partnerOptions: Array<{ value: string; label: string }>;
}

export const ReportsFilters: React.FC<IReportsFiltersProps> = ({
  form,
  onApplyFilters,
  onClearFilters,
  statusOptions,
  partnerOptions,
}) => {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleSubmit = (data: TReportsFiltersFormData) => {
    onApplyFilters(data);
  };

  return (
    <Card className="p-4 mb-6">
      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2"
          >
            <FilterIcon className="h-4 w-4" />
            {t("reports.filters.title")}
            {isExpanded ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </Button>
        </div>

        {isExpanded && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Primeira linha: Campos principais */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="sale_id"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("reports.filters.saleId")}
                        placeholder={t("reports.filters.saleIdPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("reports.filters.status")}
                        placeholder={t("reports.filters.statusPlaceholder")}
                        options={statusOptions}
                        value={field.value || undefined}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                      /> 
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partner_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Combobox
                        label={t("reports.filters.partnerName")}
                        placeholder={t("reports.filters.partnerNamePlaceholder")}
                        options={partnerOptions}
                        value={field.value || undefined}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="client_name"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("reports.filters.clientName")}
                        placeholder={t("reports.filters.clientNamePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Segunda linha: CPF, Preços e Datas */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <FormField
                control={form.control}
                name="client_cpf"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("reports.filters.clientCpf")}
                        placeholder={t("reports.filters.clientCpfPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="minimum_price"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("reports.filters.minimumPrice")}
                        placeholder={t("reports.filters.minimumPricePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="maximum_price"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <FloatingLabelInput
                        label={t("reports.filters.maximumPrice")}
                        placeholder={t("reports.filters.maximumPricePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        <FloatingLabelInput
                          label={t("reports.filters.startDate")}
                          placeholder="dd/MM/yyyy"
                          {...field}
                          mask="99/99/9999"
                          className="flex-1"
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "px-3",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              locale={ptBR}
                              selected={field.value ? parse(field.value, "dd/MM/yyyy", new Date()) : undefined}
                              onSelect={(date) => {
                                field.onChange(date ? format(date, "dd/MM/yyyy") : "");
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="flex gap-2">
                        <FloatingLabelInput
                          label={t("reports.filters.endDate")}
                          placeholder="dd/MM/yyyy"
                          {...field}
                          mask="99/99/9999"
                          className="flex-1"
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "px-3",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="h-4 w-4" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              locale={ptBR}
                              selected={field.value ? parse(field.value, "dd/MM/yyyy", new Date()) : undefined}
                              onSelect={(date) => {
                                field.onChange(date ? format(date, "dd/MM/yyyy") : "");
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClearFilters}
                className="flex items-center gap-2"
              >
                <XIcon className="h-4 w-4" />
                {t("reports.filters.clear")}
              </Button>
              <Button
                type="submit"
                className="flex items-center gap-2"
              >
                {t("reports.filters.apply")}
              </Button>
            </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};
